import "./App.css"
import { createContext, useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom"
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { lightTheme, darkTheme } from './theme';
import BottomNav from "./components/BottomNav";
import NavBar from "./components/Navbar";
import PrivateRoute from "./components/PrivateRoute";
import AddRecipe from "./pages/AddRecipe";
import Login from "./pages/Login";
import Home from "./pages/Home";
import Recipe from "./pages/Recipe";
import RecipeList from "./pages/RecipeList";
import RecipeType from "./pages/RecipeType";
import SearchResults from "./pages/SearchResults";
import Collections from "./pages/Collections";
import CollectionDetail from "./pages/CollectionDetail";
import WhatsNew from "./pages/WhatsNew";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import AuthorPage from "./pages/AuthorPage";
import AdminPage from "./pages/AdminPage";
import UserProfilePage from "./pages/UserProfilePage";
import CookPage from "./pages/CookPage";
import SignUp from "./pages/SignUp";
import PublicRecipe from "./pages/PublicRecipe";
import CookingFab from "./components/CookingFab";
import UpdateBanner from "./components/UpdateBanner";

import { refreshRecipes } from "./utils/refreshRecipes";
import { applyStoredPreferences } from "./utils/preferences";
import AnnouncementBanner from "./components/AnnouncementBanner";

export const ThemeContext = createContext({ darkMode: false, setDarkMode: () => {} });
export const useDarkMode = () => useContext(ThemeContext);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const PUBLIC_PATHS = ['/login', '/signup'];
function isPublicOnlyPath(pathname) {
  return PUBLIC_PATHS.includes(pathname) || pathname.startsWith('/r/');
}

function AppChrome() {
  const { pathname } = useLocation();
  if (isPublicOnlyPath(pathname)) return null;
  return (
    <>
      <NavBar />
      <AnnouncementBanner />
      <CookingFab />
      <BottomNav />
      <UpdateBanner />
    </>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    if (!Cookies.get('token')) return;
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser?.id) {
      fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/users/${storedUser.id}`, {
        headers: { 'Authorization': `Bearer ${Cookies.get('token')}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(freshUser => {
          if (freshUser) {
            localStorage.setItem('user', JSON.stringify({ ...storedUser, ...freshUser }));
            window.dispatchEvent(new Event('storage'));
          }
        })
        .catch(() => {})
        .finally(() => {
          applyStoredPreferences();
          setDarkMode(localStorage.getItem('darkMode') === 'true');
        });
    } else {
      applyStoredPreferences();
    }
    refreshRecipes();
  }, []);

  const handleSetDarkMode = (value) => {
    localStorage.setItem('darkMode', value);
    setDarkMode(value);
  };

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode: handleSetDarkMode }}>
      <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
        <CssBaseline />
        <Router>
          <ScrollToTop />
          <AppChrome />
          <Routes>
            <Route path="login" element={<Login />} />
            <Route path="signup" element={<SignUp />} />
            <Route path="r/:token" element={<PublicRecipe />} />
            <Route path="/" element={<PrivateRoute><Home /></PrivateRoute>} />
            <Route path="addrecipe" element={<PrivateRoute><AddRecipe /></PrivateRoute>} />
            <Route path="recipe/:id" element={<PrivateRoute><Recipe /></PrivateRoute>} />
            <Route path="search/:search_value" element={<PrivateRoute><SearchResults /></PrivateRoute>} />
            <Route path="types" element={<PrivateRoute><RecipeType /></PrivateRoute>} />
            <Route path="types/:recipe_type" element={<PrivateRoute><RecipeList /></PrivateRoute>} />
            <Route path="collections" element={<PrivateRoute><Collections /></PrivateRoute>} />
            <Route path="collections/:id" element={<PrivateRoute><CollectionDetail /></PrivateRoute>} />
            <Route path="profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
            <Route path="whats-new" element={<PrivateRoute><WhatsNew /></PrivateRoute>} />
            <Route path="author/:author_id" element={<PrivateRoute><AuthorPage /></PrivateRoute>} />
            <Route path="admin" element={<PrivateRoute><AdminPage /></PrivateRoute>} />
            <Route path="profile/:user_id" element={<PrivateRoute><UserProfilePage /></PrivateRoute>} />
            <Route path="cook" element={<PrivateRoute><CookPage /></PrivateRoute>} />
          </Routes>
        </Router>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export default App;
